import { Currency, getCurrencyByCode, SUPPORTED_CURRENCIES } from '@/constants/currencies'
import React, { useEffect, useRef, useState } from 'react'
import {
	CurrencyDropdown,
	CurrencyDropdownItem,
	CurrencyInput,
	CurrencySelectContainer,
	CurrencySelectWrapper,
	SearchIcon,
} from './CurrencySelect.styles'

interface CurrencySelectProps {
	value: string
	onChange: (currencyCode: string) => void
	label?: string
	htmlFor?: string
}

const CurrencySelect: React.FC<CurrencySelectProps> = ({ value, onChange, label, htmlFor }) => {
	const [isOpen, setIsOpen] = useState(false)
	const [searchQuery, setSearchQuery] = useState('')
	const containerRef = useRef<HTMLDivElement>(null)
	const inputRef = useRef<HTMLInputElement>(null)

	const selectedCurrency = getCurrencyByCode(value) || SUPPORTED_CURRENCIES.find(c => c.code === 'usd')!

	// Filter currencies based on search query
	const filteredCurrencies = SUPPORTED_CURRENCIES.filter(currency => {
		const query = searchQuery.toLowerCase()
		return (
			currency.code.toLowerCase().includes(query) ||
			currency.name.toLowerCase().includes(query) ||
			currency.symbol.toLowerCase().includes(query)
		)
	})

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
				setIsOpen(false)
				setSearchQuery('')
			}
		}

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside)
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside)
		}
	}, [isOpen])

	const handleSelect = (currency: Currency) => {
		onChange(currency.code)
		setIsOpen(false)
		setSearchQuery('')
	}

	const handleInputFocus = () => {
		setIsOpen(true)
	}

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSearchQuery(e.target.value)
		setIsOpen(true)
	}

	const displayValue = isOpen ? searchQuery : `${selectedCurrency.symbol} ${selectedCurrency.code.toUpperCase()} - ${selectedCurrency.name}`

	return (
		<CurrencySelectContainer>
			{label && (
				<label htmlFor={htmlFor} style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: '#374151', marginBottom: '8px' }}>
					{label}
				</label>
			)}
			<CurrencySelectWrapper ref={containerRef}>
				<CurrencyInput
					ref={inputRef}
					type="text"
					value={displayValue}
					onChange={handleInputChange}
					onFocus={handleInputFocus}
					placeholder="Search currency..."
					readOnly={!isOpen}
				/>
				<SearchIcon>
					<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
						<path
							d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667ZM14 14l-2.9-2.9"
							stroke="currentColor"
							strokeWidth="1.333"
							strokeLinecap="round"
							strokeLinejoin="round"
						/>
					</svg>
				</SearchIcon>
				{isOpen && (
					<CurrencyDropdown>
						{filteredCurrencies.length > 0 ? (
							filteredCurrencies.map(currency => (
								<CurrencyDropdownItem
									key={currency.code}
									onClick={() => handleSelect(currency)}
									$isSelected={currency.code === value}
								>
									<span style={{ fontWeight: 500 }}>
										{currency.symbol} {currency.code.toUpperCase()}
									</span>
									<span style={{ color: '#6b7280', fontSize: '14px' }}>{currency.name}</span>
								</CurrencyDropdownItem>
							))
						) : (
							<CurrencyDropdownItem $isSelected={false} style={{ cursor: 'default' }}>
								No currencies found
							</CurrencyDropdownItem>
						)}
					</CurrencyDropdown>
				)}
			</CurrencySelectWrapper>
		</CurrencySelectContainer>
	)
}

export default CurrencySelect

